"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OutputMappingInterpreter = exports.OutputMappingAnswerTypeError = exports.OutputMappingInterpreterGetOutputError = exports.TransformError = void 0;
exports.getScopesForRepeatableFrames = getScopesForRepeatableFrames;
const typegate_1 = require("typegate");
const isParentNode = (node) => {
    return !Array.isArray(node) && !!node.children;
};
const isLeafNode = (node) => {
    return !Array.isArray(node) && 'answer' in node;
};
class TransformError extends Error {
    tag = 'TransformError';
}
exports.TransformError = TransformError;
function toLowerCase(answer, path) {
    if (typeof answer === 'string') {
        return (0, typegate_1.success)(answer.toLowerCase());
    }
    return (0, typegate_1.failure)(new TransformError(`${path}: Called the toLowerCase transform on value ${answer}, which is not a string.`));
}
function yesNoToBoolean(answer, path) {
    if (['Y', 'y', 'Yes', 'yes'].includes(answer)) {
        return (0, typegate_1.success)(true);
    }
    if (['N', 'n', 'No', 'no'].includes(answer)) {
        return (0, typegate_1.success)(false);
    }
    return (0, typegate_1.failure)(new TransformError(`${path}: Called the yesNoToBoolean transform on value ${answer}, which is not a valid yes/no value.`));
}
function transformAnswer(answer, transform, path) {
    switch (transform.kind) {
        case 'LowerCase':
            return toLowerCase(answer, path);
        case 'YesNoToBoolean':
            return yesNoToBoolean(answer, path);
    }
}
function performTransforms(answer, transforms, path) {
    let transformedAnswer = answer;
    for (const transform of transforms) {
        const transformAnswerResult = transformAnswer(transformedAnswer, transform, path);
        if (transformAnswerResult.success) {
            transformedAnswer = transformAnswerResult.value;
        }
        else {
            return (0, typegate_1.failure)(transformAnswerResult.error);
        }
    }
    return (0, typegate_1.success)(transformedAnswer);
}
function validateAnswerType(type, value) {
    let result;
    switch (type) {
        case 'boolean':
            result = typegate_1.t.boolean.parse(value);
            break;
        case 'string':
            result = typegate_1.t.string.parse(value);
            break;
        case 'number':
            result = typegate_1.t.number.parse(value);
            break;
        case 'integer':
            // TODO: fix to integer
            result = typegate_1.t.number.parse(value);
            break;
        case 'null':
            result = typegate_1.t.null.parse(value);
            break;
    }
    return result;
}
function getNodeIdScopeFromFrameEnv(env) {
    const stack = [];
    const nodeIdScope = {};
    stack.push(env);
    while (stack.length) {
        const currentFrame = stack[stack.length - 1];
        stack.pop();
        for (const scopeKey in currentFrame.scope) {
            if (!nodeIdScope[scopeKey]) {
                nodeIdScope[scopeKey] = currentFrame.scope[scopeKey];
            }
        }
        if (currentFrame.parent) {
            stack.push(currentFrame.parent);
        }
    }
    return nodeIdScope;
}
function resolveScope(parentEnv, keyToRemove) {
    const stack = [];
    stack.push(parentEnv);
    while (stack.length) {
        const currentEnv = stack[stack.length - 1];
        stack.pop();
        if (keyToRemove in parentEnv.scope) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [keyToRemove]: _, ...newEnvScope } = parentEnv.scope;
            parentEnv.scope = newEnvScope;
        }
        if (currentEnv.parent) {
            stack.push(currentEnv.parent);
        }
    }
    return parentEnv;
}
function getScopesForRepeatableFrames(scope, answersResolver, parentEnv) {
    let keys = Object.keys(scope);
    const indicesForEachNestedScope = [];
    for (const key in scope) {
        if (scope[key] === 'n') {
            const parentScope = parentEnv ? getNodeIdScopeFromFrameEnv(parentEnv) : {};
            const repetitionCount = answersResolver.usingNodeId().getRepetitionCount(key, parentScope) || 0;
            const scopeIndices = [];
            for (let i = 0; i < repetitionCount; i++) {
                scopeIndices.push(i);
            }
            indicesForEachNestedScope.push(scopeIndices);
        }
        else if (typeof scope[key] === 'number') {
            indicesForEachNestedScope.push([scope[key]]);
        }
        else if (scope[key] === '-') {
            if (parentEnv) {
                const newParentEnv = resolveScope(parentEnv, key);
                const nodeIdScope = newParentEnv ? getNodeIdScopeFromFrameEnv(newParentEnv) : {};
                keys = Object.keys(nodeIdScope);
                for (const key of keys) {
                    indicesForEachNestedScope.push([nodeIdScope[key]]);
                }
            }
        }
    }
    const frameScopes = [];
    const stack = [];
    if (!indicesForEachNestedScope.length) {
        return frameScopes;
    }
    for (const value of indicesForEachNestedScope[0]) {
        stack.push({
            currentPick: [value],
            scopePositionIndex: 0,
        });
    }
    while (stack.length) {
        const { currentPick, scopePositionIndex } = stack[stack.length - 1];
        stack.pop();
        if (currentPick.length === indicesForEachNestedScope.length) {
            const scope = {};
            for (const [index, key] of keys.entries()) {
                scope[key] = currentPick[index];
            }
            frameScopes.push(scope);
        }
        else {
            for (const value of indicesForEachNestedScope[scopePositionIndex + 1]) {
                stack.push({
                    currentPick: [...currentPick, value],
                    scopePositionIndex: scopePositionIndex + 1,
                });
            }
        }
    }
    return frameScopes;
}
class OutputMappingInterpreterGetOutputError extends Error {
    tag = 'OutputMappingInterpreterGetOutputError';
    errors = [];
    constructor(errors, msg) {
        super(msg);
        this.errors = errors;
    }
}
exports.OutputMappingInterpreterGetOutputError = OutputMappingInterpreterGetOutputError;
class OutputMappingAnswerTypeError extends Error {
    tag = 'OutputMappingAnswerTypeError';
}
exports.OutputMappingAnswerTypeError = OutputMappingAnswerTypeError;
class OutputMappingInterpreter {
    outputMapping;
    answersResolver;
    identifierType;
    options;
    constructor(outputMapping, answersResolver, options) {
        this.outputMapping = outputMapping;
        this.answersResolver = answersResolver;
        this.identifierType = outputMapping.identifierType;
        this.options = options || {};
    }
    getOutput() {
        const output = {};
        const stack = [];
        const rootNode = this.outputMapping.root;
        const errors = [];
        const rootFrame = {
            node: rootNode,
            outputPointer: output,
            env: null,
        };
        stack.push(rootFrame);
        while (stack.length) {
            const currentFrame = stack[stack.length - 1];
            const currentNode = currentFrame.node;
            if (Array.isArray(currentNode) && rootNode === currentNode) {
                stack.pop();
                const outputPointer = currentFrame.outputPointer;
                for (let i = currentNode.length - 1; i >= 0; i--) {
                    stack.push({ node: currentNode[i], outputPointer, env: null });
                }
            }
            if (isParentNode(currentNode)) {
                stack.pop();
                if (currentNode.scope) {
                    currentFrame.outputPointer[currentNode.path] = [];
                    const repeatableScope = getScopesForRepeatableFrames(currentNode.scope, this.answersResolver, currentFrame.env);
                    for (const key of repeatableScope) {
                        currentFrame.outputPointer[currentNode.path].unshift({});
                        const outputPointer = currentFrame.outputPointer[currentNode.path][0];
                        for (let j = currentNode.children.length - 1; j >= 0; j--) {
                            stack.push({
                                node: currentNode.children[j],
                                outputPointer,
                                env: { scope: key, parent: currentFrame.env },
                            });
                        }
                    }
                }
                else if (!(currentNode.path in currentFrame.outputPointer)) {
                    currentFrame.outputPointer[currentNode.path] = {};
                    const outputPointer = currentFrame.outputPointer[currentNode.path];
                    for (let i = currentNode.children.length - 1; i >= 0; i--) {
                        stack.push({ node: currentNode.children[i], outputPointer, env: { scope: {}, parent: currentFrame.env } });
                    }
                }
            }
            if (isLeafNode(currentNode)) {
                stack.pop();
                let answer;
                if (currentNode.scope && Object.keys(currentNode.scope).length) {
                    const repeatableScopes = getScopesForRepeatableFrames(currentNode.scope, this.answersResolver, currentFrame.env);
                    const scope = repeatableScopes[0];
                    answer = this.answersResolver.usingNodeId().getAnswer(currentNode.answer, scope);
                }
                else {
                    let nodeIdScope = {};
                    if (currentFrame.env) {
                        nodeIdScope = getNodeIdScopeFromFrameEnv(currentFrame.env);
                    }
                    answer = this.answersResolver.usingNodeId().getAnswer(currentNode.answer, nodeIdScope);
                }
                if (currentNode.transform) {
                    const transformAnswerResult = performTransforms(answer, currentNode.transform, currentNode.path);
                    if (transformAnswerResult.success) {
                        answer = transformAnswerResult.value;
                    }
                    else {
                        errors.push(transformAnswerResult.error);
                    }
                }
                if (currentNode.type) {
                    const validationResult = validateAnswerType(currentNode.type, answer);
                    if (!validationResult.success) {
                        errors.push(new OutputMappingAnswerTypeError(`${currentNode.path}: ${validationResult.error}`));
                    }
                    else {
                        currentFrame.outputPointer[currentNode.path] = answer;
                    }
                }
                if (this.options.setUndefinedAnswers || answer !== undefined) {
                    currentFrame.outputPointer[currentNode.path] = answer;
                }
            }
        }
        if (errors.length) {
            return (0, typegate_1.failure)(new OutputMappingInterpreterGetOutputError(errors));
        }
        return (0, typegate_1.success)(output);
    }
}
exports.OutputMappingInterpreter = OutputMappingInterpreter;
